import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import LoginPage from "@/pages/LoginPage";
import DashboardPage from "@/pages/DashboardPage";
import ProjectListPage from "@/pages/ProjectListPage";
import ProjectCreatePage from "@/pages/ProjectCreatePage";
import ProjectDetailPage from "@/pages/ProjectDetailPage";
import AdminPage from "@/pages/AdminPage";
import AdminTemplatesPage from "@/pages/AdminTemplatesPage";
import ReportsPage from "@/pages/ReportsPage";
import MapPage from "@/pages/MapPage";
import IncidentsListPage from "@/pages/IncidentsListPage";
import IncidentCreatePage from "@/pages/IncidentCreatePage";
import IncidentDetailPage from "@/pages/IncidentDetailPage";
import ProfilePage from "@/pages/ProfilePage";
import AuditLogPage from "@/pages/AuditLogPage";
import HealthPage from "@/pages/HealthPage";
import NotificationBoot from "@/components/NotificationBoot";
import SharePage from "@/pages/SharePage";
import UploadStatusPanel from "@/components/UploadStatusPanel";
import { installSyncListeners } from "@/lib/syncQueue";
import "@/App.css";

installSyncListeners();

function Protected({ children, roles }) {
  const { user } = useAuth();
  if (!user) return <Navigate to="/login" replace />;
  if (roles && !roles.includes(user.role)) return <Navigate to="/" replace />;
  return children;
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <NotificationBoot />
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          {/* Public, no-auth customer download page. Mounted OUTSIDE the
              <Protected> wrapper so an unauthenticated visitor with the
              right share token can land on it directly. */}
          <Route path="/share/:token" element={<SharePage />} />
          {/* Self-registration is admin-only now — users are created from
              the Admin page. Bounce the old /register URL to /login so
              any bookmarked link still lands somewhere usable. */}
          <Route path="/register" element={<Navigate to="/login" replace />} />
          <Route path="/" element={<Protected><DashboardPage /></Protected>} />
          <Route path="/projects" element={<Protected><ProjectListPage /></Protected>} />
          <Route path="/projects/new" element={<Protected><ProjectCreatePage /></Protected>} />
          <Route path="/projects/:id" element={<Protected><ProjectDetailPage /></Protected>} />
          <Route path="/profile" element={<Protected><ProfilePage /></Protected>} />
          <Route path="/admin" element={<Protected roles={["admin"]}><AdminPage /></Protected>} />
          <Route path="/admin/templates" element={<Protected roles={["admin"]}><AdminTemplatesPage /></Protected>} />
          <Route path="/audit" element={<Protected roles={["admin"]}><AuditLogPage /></Protected>} />
          <Route path="/admin/health" element={<Protected roles={["admin"]}><HealthPage /></Protected>} />
          <Route path="/reports" element={<Protected roles={["admin", "pm", "foreman"]}><ReportsPage /></Protected>} />
          <Route path="/map" element={<Protected><MapPage /></Protected>} />
          <Route path="/incidents" element={<Protected><IncidentsListPage /></Protected>} />
          <Route path="/incidents/new" element={<Protected><IncidentCreatePage /></Protected>} />
          <Route path="/incidents/:id" element={<Protected><IncidentDetailPage /></Protected>} />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      <Toaster richColors theme="dark" position="top-right" />
      <UploadStatusPanel />
    </AuthProvider>
  );
}

export default App;
